/**
 * Home Screen Data Hook
 *
 * Fetches aggregated home screen data using the Hono RPC client
 * with TanStack Query for caching and state management.
 */

import { useQuery, queryOptions } from "@tanstack/react-query";
import { api, unwrapResponse, ApiError } from "@/lib/api";

// ============================================================================
// Types
// ============================================================================

/**
 * Portfolio summary data
 */
export interface PortfolioSummary {
  totalValue: number;
  totalInvested: number;
  totalReturns: number;
  totalReturnsPercent: number;
  dailyChange: number;
  dailyChangePercent: number;
  xirr: number;
}

/**
 * Holdings summary with rating breakdown
 */
export interface HoldingsSummary {
  count: number;
  ratingBreakdown: {
    "in-form": number;
    "on-track": number;
    "off-track": number;
    "out-of-form": number;
  };
}

/**
 * Top performer item
 */
export interface TopPerformer {
  fundName: string;
  returns: number;
  currentValue: number;
  rating: "in-form" | "on-track" | "off-track" | "out-of-form";
}

/**
 * Allocation breakdown by category
 */
export interface AllocationBreakdown {
  equityLargeCap: number;
  equityMidCap: number;
  equitySmallCap: number;
  equityGlobal: number;
  debt: number;
  others: number;
}

/**
 * Recommendation item (high priority only for home screen)
 */
export interface HomeRecommendation {
  id: string;
  type: "switch" | "increase-sip" | "exit" | "hold";
  reason: string;
  impactDescription: string;
  priority: "high" | "medium" | "low";
}

/**
 * Complete home screen data
 */
export interface HomeScreenData {
  portfolio: PortfolioSummary;
  holdings: HoldingsSummary;
  topPerformers: TopPerformer[];
  recommendations: HomeRecommendation[];
  allocation: AllocationBreakdown;
}

// ============================================================================
// Query Options
// ============================================================================

/**
 * Query options for home screen data
 * Can be used with useQuery or prefetching
 */
export const homeDataQueryOptions = queryOptions({
  queryKey: ["home", "data"] as const,
  queryFn: async (): Promise<HomeScreenData> => {
    const response = await api.v1.home.$get();
    return unwrapResponse<HomeScreenData>(response);
  },
  staleTime: 1000 * 60 * 5, // 5 minutes
  gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
});

/**
 * Query options for portfolio summary only (lighter endpoint)
 */
export const portfolioSummaryQueryOptions = queryOptions({
  queryKey: ["home", "summary"] as const,
  queryFn: async (): Promise<PortfolioSummary> => {
    const response = await api.v1.home.summary.$get();
    return unwrapResponse<PortfolioSummary>(response);
  },
  staleTime: 1000 * 60 * 2, // 2 minutes (more frequently updated)
  gcTime: 1000 * 60 * 15, // 15 minutes
});

/**
 * Query options for allocation data
 */
export const allocationQueryOptions = queryOptions({
  queryKey: ["home", "allocation"] as const,
  queryFn: async () => {
    const response = await api.v1.home.allocation.$get();
    return unwrapResponse<{
      breakdown: AllocationBreakdown;
      sectors: Array<{ sector: string; percentage: number; value: number }>;
      topHoldings: Array<{ name: string; percentage: number; value: number }>;
    }>(response);
  },
  staleTime: 1000 * 60 * 10, // 10 minutes (less frequently updated)
  gcTime: 1000 * 60 * 60, // 1 hour
});

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching home screen data
 *
 * @example
 * ```tsx
 * function HomeScreen() {
 *   const { data, isLoading, error, refetch } = useHomeData();
 *
 *   if (isLoading) return <HomeSkeleton />;
 *   if (error) return <ErrorDisplay error={error} onRetry={refetch} />;
 *
 *   return (
 *     <div>
 *       <PortfolioSummaryCard portfolio={data.portfolio} />
 *       <HoldingsOverview holdings={data.holdings} />
 *     </div>
 *   );
 * }
 * ```
 */
export function useHomeData() {
  return useQuery(homeDataQueryOptions);
}

/**
 * Hook for fetching portfolio summary only
 * Use this when you only need the portfolio stats (e.g., for a widget)
 */
export function usePortfolioSummary() {
  return useQuery(portfolioSummaryQueryOptions);
}

/**
 * Hook for fetching allocation data
 * Includes breakdown, sectors, and top holdings
 */
export function useAllocation() {
  return useQuery(allocationQueryOptions);
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Type guard for ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    if (error.status === 404) {
      return "Data not found. Please try again later.";
    }
    if (error.status >= 500) {
      return "Server error. Please try again later.";
    }
    return error.message;
  }

  if (error instanceof Error) {
    if (error.message.includes("fetch")) {
      return "Network error. Please check your connection.";
    }
    return error.message;
  }

  return "An unexpected error occurred.";
}
